import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../hooks/useEntitlements';
import { useAuth } from '../AuthContext';
import { SuccessPageSkeleton, LoadingButtonContent } from './LoadingComponents';
import { useToast } from '../contexts/ToastContext';
import { createAuthError, createTokenError, parseErrorMessage } from '../utils/errorHandling';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { motion } from 'framer-motion';

interface UpgradeSuccessPageProps {
  onNavigate?: (section: string) => void;
}

// Shared gradient background matching the pricing flow
const GradientBackground: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-600/10 rounded-full blur-3xl" />
    </div>
    <div className="relative z-10 w-full max-w-xl px-6 flex flex-col items-center">
      {children}
    </div>
  </div>
);

// Premium feature list
const premiumFeatures = [
  { lottieSrc: '/materials/icons/chapter.json', title: 'Kompletní učebnice' },
  { lottieSrc: '/materials/icons/flashcardlight.json', title: 'Flashcards a testy' },
  { lottieSrc: '/materials/icons/audio book.lottie', title: 'Audioknihy' },
  { lottieSrc: '/materials/icons/wired-flat-1865-shooting-stars-hover-pinch.json', title: 'AI Tutor 24/7' },
];

const UpgradeSuccessPage: React.FC<UpgradeSuccessPageProps> = ({ onNavigate }) => {
  const { user, hasPro, loading, error: entitlementsError, retry } = useEntitlements();
  const { signInWithGoogle } = useAuth();
  const { showError, showSuccess, showInfo, showWarning } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showRetryGuidance, setShowRetryGuidance] = useState(false);

  // Preview mode: /upgrade-success?preview=true bypasses all checks
  const isPreview = new URLSearchParams(window.location.search).get('preview') === 'true';
  const showSuccess_ = isPreview || hasPro;

  // Refresh Firebase ID token on mount
  useEffect(() => {
    const refreshToken = async () => {
      if (user) {
        try {
          setIsRefreshing(true);
          showInfo('Synchronizuji nákup...', 'Ověřujeme tvůj status.');
          await user.getIdToken(true);
        } catch (error) {
          console.error('Error refreshing token:', error);
          const errorInfo = parseErrorMessage(createTokenError('Problém s ověřením. Zkus obnovit stránku.'));
          showError(errorInfo.title, errorInfo.message);
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    refreshToken();
  }, [user, showInfo, showError]);

  // Show retry guidance after 30 seconds if Pro status is not active
  useEffect(() => {
    if (user && !showSuccess_ && !loading) {
      const timer = setTimeout(() => {
        setShowRetryGuidance(true);
        showWarning(
          'Synchronizace trvá déle než obvykle',
          'Zkus obnovit stav nebo počkej chvilku. Někdy může zpracování platby trvat několik minut.'
        );
      }, 30000);

      return () => clearTimeout(timer);
    }
  }, [user, hasPro, loading, showWarning, showSuccess_]);



  // Navigation
  const handleBackToDashboard = () => {
    if (onNavigate) {
      onNavigate('home');
    }
  };

  // Sign-in for signed-out users
  const handleSignIn = async () => {
    try {
      showInfo('Přihlašování...', 'Přesměrováváme tě na přihlášení přes Google.');
      await signInWithGoogle();
      showSuccess('Přihlášení úspěšné!', 'Nyní můžeme dokončit synchronizaci tvého nákupu.');
    } catch (error) {
      console.error('Sign-in failed:', error);
      const errorInfo = parseErrorMessage(createAuthError('Přihlášení se nezdařilo. Zkus to prosím znovu.'));
      showError(errorInfo.title, errorInfo.message);
    }
  };

  // Manual token refresh
  const handleRetryRefresh = async () => {
    if (user) {
      try {
        setIsRefreshing(true);
        showInfo('Obnovuji stav...', 'Kontrolujeme tvůj status znovu.');
        await user.getIdToken(true);
        setShowRetryGuidance(false);
        showSuccess('Stav obnoven', 'Zkontrolovali jsme tvůj status.');
      } catch (error) {
        console.error('Error refreshing token:', error);
        const errorInfo = parseErrorMessage(createTokenError('Problém s obnovením stavu. Zkus to znovu.'));
        showError(errorInfo.title, errorInfo.message);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  // Loading skeleton
  if (loading && !user) {
    return <SuccessPageSkeleton />;
  }

  // ─── NOT SIGNED IN ───
  if (!user) {
    return (
      <GradientBackground>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mx-auto mb-6 w-20 h-20 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center"
        >
          <svg className="w-10 h-10 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-3xl sm:text-4xl font-bold text-white text-center mb-4"
        >
          Přihlas se prosím
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-lg text-white/60 text-center mb-8"
        >
          Pro dokončení nákupu se musíš přihlásit do svého účtu.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="w-full space-y-4"
        >
          <button
            onClick={handleSignIn}
            className="w-full bg-white hover:bg-gray-50 text-gray-900 text-base sm:text-lg font-bold py-3.5 sm:py-4 rounded-2xl transition-all border-b-[6px] border-gray-300 active:border-b-2 active:translate-y-[4px] shadow-lg shadow-white/10"
          >
            Přihlásit přes Google
          </button>

          <button
            onClick={handleBackToDashboard}
            className="w-full text-white/40 hover:text-white/60 text-sm font-medium transition-colors"
          >
            Zpět do dashboardu
          </button>
        </motion.div>
      </GradientBackground>
    );
  }

  // ─── PRO CONFIRMED — SUCCESS! ───
  if (showSuccess_) {
    return (
      <GradientBackground>
        {/* Trophy Animation */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="w-36 h-36 sm:w-48 sm:h-48 mb-4"
        >
          <DotLottieReact
            src="/materials/Trophy.lottie"
            loop={false}
            autoplay={true}
            className="w-full h-full"
          />
        </motion.div>

        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-3xl sm:text-4xl font-bold text-white text-center mb-3"
        >
          Gratulujeme! 🎉
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-lg sm:text-xl text-white/70 text-center mb-8"
        >
          Tvůj Premium přístup je aktivní!
        </motion.p>

        {/* Feature Cards Grid */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                delay: 0.5,
                staggerChildren: 0.1
              }
            }
          }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full mb-8"
        >
          {premiumFeatures.map((feature) => (
            <motion.div
              key={feature.title}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              transition={{ duration: 0.3 }}
              className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4"
            >
              <div className="w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <DotLottieReact
                  src={feature.lottieSrc}
                  loop={false}
                  autoplay={false}
                  className="w-9 h-9 sm:w-12 sm:h-12"
                />
              </div>
              <h3 className="text-white font-bold text-sm sm:text-base leading-tight">{feature.title}</h3>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Button — Duolingo Style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.4 }}
          className="w-full"
        >
          <button
            onClick={handleBackToDashboard}
            className="w-full bg-white hover:bg-gray-50 text-gray-900 text-base sm:text-lg font-bold py-3.5 sm:py-4 rounded-2xl transition-all border-b-[6px] border-gray-300 active:border-b-2 active:translate-y-[4px] shadow-lg shadow-white/10"
          >
            Jdeme studovat! 🚀
          </button>
        </motion.div>
      </GradientBackground>
    );
  }

  // ─── SYNC / WAITING FOR PRO STATUS ───
  return (
    <GradientBackground>
      {/* Spinner */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin" />
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="text-2xl sm:text-3xl font-bold text-white text-center mb-3"
      >
        {isRefreshing || loading ? 'Synchronizujeme tvůj nákup...' : 'Ověřujeme tvůj Premium status...'}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="text-white/50 text-center mb-8"
      >
        Chvilku strpení, může to trvat několik sekund.
      </motion.p>

      {/* Entitlements error */}
      {entitlementsError && (
        <div className="w-full rounded-2xl bg-red-500/10 backdrop-blur-md border border-red-400/20 p-5 mb-6">
          <h2 className="text-lg font-semibold text-red-300 mb-2">
            Problém s načítáním dat
          </h2>
          <p className="text-sm text-red-200/80 mb-4">
            {entitlementsError}
          </p>
          <button
            onClick={retry}
            className="rounded-xl px-5 py-2.5 font-bold text-sm bg-red-500/20 hover:bg-red-500/30 text-red-200 transition-colors border border-red-400/30"
          >
            Zkusit znovu
          </button>
        </div>
      )}

      {/* Retry guidance after 30 seconds */}
      {showRetryGuidance && (
        <div className="w-full rounded-2xl bg-yellow-500/10 backdrop-blur-md border border-yellow-400/20 p-5 mb-6">
          <h2 className="text-lg font-semibold text-yellow-300 mb-2">
            Synchronizace trvá déle než obvykle
          </h2>
          <p className="text-sm text-yellow-200/80 mb-4">
            Někdy může zpracování platby trvat několik minut. Můžeš zkusit obnovit stav nebo počkat chvilku.
          </p>
          <div className="space-y-3">
            <button
              onClick={handleRetryRefresh}
              disabled={isRefreshing}
              className="w-full rounded-xl px-5 py-2.5 font-bold text-sm bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white transition-colors border border-white/10 flex items-center justify-center"
            >
              <LoadingButtonContent
                isLoading={isRefreshing}
                loadingText="Obnovuji…"
                defaultText="Zkusit znovu"
              />
            </button>
            <div className="text-xs text-yellow-300/60 text-center">
              Pokud problém přetrvává, kontaktuj podporu: podpora@opravech.cz
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleBackToDashboard}
        className="text-white/40 hover:text-white/60 text-sm font-medium transition-colors"
      >
        Zpět do dashboardu
      </button>
    </GradientBackground>
  );
};

export default UpgradeSuccessPage;